import React, { useState, useEffect, useCallback, useRef } from "react";
import { PhoneCall, Upload } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Chip,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  CloudUpload as UploadIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";

export default function ContactCaller() {
  const [contacts, setContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calling, setCalling] = useState({});
  const fetchedRef = useRef(false);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/contacts-list`
      );
      const data = await res.json();

      if (data.status && Array.isArray(data.data)) {
        const formatted = data.data.map((c, i) => ({
          id: c._id || i,
          name: c.name || "Unknown",
          countryCode: c.country_code || "+91",
          phone: c.phone || "",
          createdAt: c.created_at || "",
        }));
        setContacts(formatted);
        toast.success(data.message || "Contacts fetched successfully");
      }
    } catch (err) {
      console.error("Fetch contacts error:", err);
      toast.error("Error fetching contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchContacts();
    }
  }, [fetchContacts]);

  const handleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.length === contacts.length ? [] : contacts.map((c) => c.id)
    );
  }, [contacts]);

  const handleTraditionalCall = async (contact) => {
    const contactId = contact.id;
    const phoneNumber = contact.phone || "";
    const fullNumber = phoneNumber.startsWith("+")
      ? phoneNumber
      : `${contact.countryCode || "+91"}${phoneNumber}`;

    console.log("🔥 Traditional Call Started");
    console.log("Contact:", contact);
    console.log("Full Number:", fullNumber);

    setCalling((prev) => ({ ...prev, [contactId]: true }));
    const toastId = toast.loading(`Calling ${contact.name}...`);

    try {
      const payload = {
        caller: "+911203214860",
        destination: fullNumber,
      };

      console.log("📤 Sending API Request:", payload);

      const res = await fetch(
        `${process.env.REACT_APP_VOIP_SERVER_BASE_URL}/customoutbound`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      console.log("📥 Response Status:", res.status);

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      console.log("📥 Response Data:", data);

      toast.success(
        data.message || `Call to ${contact.name} initiated successfully!`,
        { id: toastId }
      );
    } catch (error) {
      console.error("❌ Call Error:", error);
      toast.error(`Failed to call ${contact.name}: ${error.message}`, {
        id: toastId,
      });
    } finally {
      setCalling((prev) => ({ ...prev, [contactId]: false }));
    }
  };

  const handleAgentCall = useCallback((contact) => {
    const phoneNumber = contact.phone || "";
    const fullNumber = phoneNumber.startsWith("+")
      ? phoneNumber
      : `${contact.countryCode || "+91"}${phoneNumber}`;
    toast.success(`Agent call to ${contact.name} (${fullNumber})`);
    console.log("📞 Agent Call:", contact);
  }, []);

  const handleCallSelected = useCallback(() => {
    console.log("🚀 Call Selected Contacts Initiated");
    if (selectedIds.length === 0) {
      toast.error("Please select at least one contact to call!");
      return;
    }
    const selectedContacts = contacts.filter((c) => selectedIds.includes(c.id));
    const names = selectedContacts.map((c) => c.name).join(", ");
    toast.success(`Calling selected contacts: ${names}`);
    console.log("📞 Calling selected contacts:", selectedContacts);
  }, [contacts, selectedIds]);

  const handleImportExcel = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        toast.loading("Importing contacts...", { id: "import" });
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/contacts/import`,
          {
            method: "POST",
            body: formData,
          }
        );
        const data = await res.json();
        toast.dismiss("import");

        if (data.success) {
          toast.success(data.message || "Contacts imported successfully!");
          fetchContacts();
        } else {
          toast.error(data.message || "Failed to import contacts.");
        }
      } catch (error) {
        toast.dismiss("import");
        console.error("Excel import error:", error);
        toast.error("Error uploading Excel file.");
      } finally {
        e.target.value = "";
      }
    },
    [fetchContacts]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Toaster position="top-right" />

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <PhoneIcon sx={{ color: "white", fontSize: 28 }} />
            <Typography variant="h5" sx={{ color: "white", fontWeight: 600 }}>
              Contact Caller
            </Typography>
            {contacts.length > 0 && (
              <Chip
                label={`${contacts.length} contacts`}
                size="small"
                sx={{
                  ml: 1,
                  backgroundColor: alpha("#fff", 0.2),
                  color: "white",
                  fontWeight: 500,
                }}
              />
            )}
          </Box>

          <Box>
            <input
              id="excel-upload"
              type="file"
              accept=".xlsx, .xls"
              onChange={handleImportExcel}
              style={{ display: "none" }}
            />
            <label htmlFor="excel-upload">
              <Button
                component="span"
                variant="contained"
                startIcon={<UploadIcon />}
                sx={{
                  backgroundColor: "white",
                  boxShadow: "none",
                  color: "#667eea",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: alpha("#fff", 0.9),
                  },
                }}
              >
                Import Excel
              </Button>
            </label>
          </Box>
        </Box>

        {/* Table */}
        <TableContainer>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 8,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                  <TableCell padding="checkbox" sx={{ width: 50 }}>
                    <Checkbox
                      checked={
                        contacts.length > 0 &&
                        selectedIds.length === contacts.length
                      }
                      indeterminate={
                        selectedIds.length > 0 &&
                        selectedIds.length < contacts.length
                      }
                      onChange={handleSelectAll}
                      sx={{
                        color: "#9CA3AF",
                        "&.Mui-checked": { color: "#667eea" },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                    Country Code
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                    Phone
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: "#374151" }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <PersonAddIcon
                        sx={{ fontSize: 48, color: "#D1D5DB", mb: 2 }}
                      />
                      <Typography variant="body1" color="text.secondary">
                        No contacts found. Import contacts to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  contacts.map((contact, index) => (
                    <TableRow
                      key={contact.id}
                      sx={{
                        "&:hover": { backgroundColor: "#F9FAFB" },
                        backgroundColor:
                          selectedIds.includes(contact.id)
                            ? alpha("#667eea", 0.05)
                            : "transparent",
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(contact.id)}
                          onChange={() => handleSelect(contact.id)}
                          sx={{
                            color: "#9CA3AF",
                            "&.Mui-checked": { color: "#667eea" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {contact.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={contact.countryCode}
                          size="small"
                          sx={{
                            backgroundColor: "#F3F4F6",
                            color: "#6B7280",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: "#6B7280", fontFamily: "monospace" }}
                        >
                          {contact.phone}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                          <Tooltip title="Traditional Call">
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={
                                calling[contact.id] ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <PhoneIcon fontSize="small" />
                                )
                              }
                              onClick={() => handleTraditionalCall(contact)}
                              disabled={calling[contact.id]}
                              sx={{
                                backgroundColor: "#10B981",
                                boxShadow: "none",
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "0.813rem",
                                px: 2, py: 1,
                                "&:hover": {
                                  backgroundColor: "#059669",
                                },
                                "&:disabled": {
                                  backgroundColor: "#D1FAE5",
                                  color: "#6EE7B7",
                                },
                              }}
                            >
                              {calling[contact.id]
                                ? "Calling..."
                                : "Traditional"}
                            </Button>
                          </Tooltip>

                          <Tooltip title="Agent Call">
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PhoneIcon fontSize="small" />}
                              onClick={() => handleAgentCall(contact)}
                              disabled={calling[contact.id]}
                              sx={{
                                backgroundColor: "#3B82F6",
                                boxShadow: "none",
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "0.813rem",
                                px: 2,
                                "&:hover": {
                                  backgroundColor: "#2563EB",
                                },
                              }}
                            >
                              Agent
                            </Button>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Footer */}
        {contacts.length > 0 && (
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#F9FAFB",
            }}
          >
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              {selectedIds.length > 0
                ? `${selectedIds.length} of ${contacts.length} selected`
                : `Total ${contacts.length} contacts`}
            </Typography>

            <Button
              variant="contained"
              startIcon={<PhoneIcon />}
              onClick={handleCallSelected}
              disabled={selectedIds.length === 0}
              sx={{
                backgroundColor: "#667eea",
                textTransform: "none",
                fontWeight: 500,
                px: 3,
                "&:hover": {
                  backgroundColor: "#5568d3",
                },
                "&:disabled": {
                  backgroundColor: "#E5E7EB",
                  color: "#9CA3AF",
                },
              }}
            >
              Call Selected ({selectedIds.length})
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
